import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import api from '../services/api';

interface PublicHCSettings {
  display_name?: string;
  logo_url?: string;
  brand_color?: string;
}

const HelpCenterPasswordGate: React.FC = () => {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [branding, setBranding] = useState<PublicHCSettings>({});

  const redirectTo = searchParams.get('redirect') ?? `/help/${workspaceSlug}`;

  useEffect(() => {
    if (workspaceSlug) {
      // Try to get branding from public help center endpoint.
      // Even if it returns 403 (password protected), branding may be in the response body.
      api
        .get<any>(`/public/help-center/${workspaceSlug}`)
        .then(res => {
          const d = res.data;
          setBranding({
            display_name: d.display_name ?? d.workspace?.display_name,
            logo_url: d.logo_url ?? d.workspace?.logo_url,
            brand_color: d.brand_color ?? d.workspace?.brand_color,
          });
        })
        .catch(err => {
          // 403 response body may still contain branding
          const d = err?.response?.data;
          if (d) {
            setBranding({
              display_name: d.display_name ?? d.workspace?.display_name,
              logo_url: d.logo_url ?? d.workspace?.logo_url,
              brand_color: d.brand_color ?? d.workspace?.brand_color,
            });
          }
        });
    }
  }, [workspaceSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post<{ ok: boolean; error?: string }>(`/public/help-center/${workspaceSlug}/auth`, { password });
      if (!res.data.ok) {
        setError('Incorrect password. Please try again.');
        return;
      }
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError('Incorrect password. Please try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const brandColor = branding.brand_color ?? '#6366f1';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center px-4">
      {/* Branding header */}
      <div className="text-center mb-8">
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.display_name ?? 'Help Center'}
            className="h-12 w-auto mx-auto mb-4 object-contain"
          />
        ) : (
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: brandColor }}
          >
            <Lock className="w-7 h-7 text-white" />
          </div>
        )}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {branding.display_name ?? 'Help Center'}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          This Help Center is password protected
        </p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 mx-auto mb-5">
          <Lock className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        </div>
        <h2 className="text-center text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Enter the portal password
        </h2>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6">
          Contact your workspace admin if you don't have one.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="Password"
              autoFocus
              className={`w-full px-4 py-3 pr-11 rounded-xl border text-sm bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 transition-colors ${
                error
                  ? 'border-red-400 dark:border-red-500 focus:ring-red-400'
                  : 'border-gray-200 dark:border-gray-700 focus:ring-indigo-500'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !password.trim()}
            style={{ backgroundColor: brandColor }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying…
              </>
            ) : (
              'Access Help Center'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default HelpCenterPasswordGate;
